/**
 * Blog List Admin Page
 * 
 * Admin interface for managing all blog posts.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useAdminBlogList } from '../../hooks/useAdminBlogList';
import ProtectedRoute from '../../components/admin/ProtectedRoute';
import Breadcrumbs from '../../components/admin/Breadcrumbs';
import LoadingSkeleton from '../../components/common/LoadingSkeleton';
import { useNotifications } from '../../context/NotificationContext';

const BlogListAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState<string | null>(null);

  const { blogs, isLoading, error, deleteBlog } = useAdminBlogList({
    includeDrafts: true,
    search: searchTerm || undefined,
  });
  const { showSuccess, showError } = useNotifications();

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Handle delete click
   */
  const handleDeleteClick = (id: string) => {
    setBlogToDelete(id);
    setDeleteDialogOpen(true);
  };

  /**
   * Handle delete confirmation
   */
  const handleDeleteConfirm = async () => {
    if (blogToDelete) {
      try {
        await deleteBlog(blogToDelete);
        setDeleteDialogOpen(false);
        setBlogToDelete(null);
        showSuccess('Blog post deleted successfully');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete blog post';
        showError(errorMessage);
      }
    }
  };

  /**
   * Handle edit click
   */
  const handleEdit = (id: string) => {
    navigate(`/admin/blog/edit/${id}`);
  };

  /**
   * Handle create new
   */
  const handleCreateNew = () => {
    navigate('/admin/blog/new');
  };

  return (
    <ProtectedRoute>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: 'Admin', path: '/admin' },
            { label: 'Blog Management' },
          ]}
        />

        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" component="h1">
            Blog Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            New Post
          </Button>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading ? (
          <LoadingSkeleton count={5} height={80} />
        ) : (
          /* Blog Posts Table */
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Published Date</TableCell>
                  <TableCell>Featured</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No blog posts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  blogs.map((blog) => (
                    <TableRow key={blog._id} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {blog.title}
                        </Typography>
                        {blog.excerpt && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            {blog.excerpt.substring(0, 100)}
                            {blog.excerpt.length > 100 ? '...' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={blog.status === 'published' ? 'Published' : 'Draft'}
                          color={blog.status === 'published' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {blog.publishedAt || blog.publishedDate
                          ? formatDate(blog.publishedAt || blog.publishedDate)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {blog.categories?.slice(0, 2).map((cat, idx) => (
                            <Chip
                              key={idx}
                              label={cat}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {blog.categories && blog.categories.length > 2 && (
                            <Chip
                              label={`+${blog.categories.length - 2}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {blog.isFeatured && (
                          <Chip label="Featured" color="primary" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(blog._id)}
                          color="primary"
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(blog._id)}
                          color="error"
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setBlogToDelete(null);
          }}
        >
          <DialogTitle>Delete Blog Post</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this blog post? This action
              cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setBlogToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ProtectedRoute>
  );
};

export default BlogListAdmin;

